<template>
  <canvas ref="canvasRef" class="home-backdrop-canvas" aria-hidden="true" />
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

interface Particle {
  baseX: number
  y: number
  spawnY: number
  vy: number
  swayAmp: number
  swayFreq: number
  phase: number
  size: number
  peakOpacity: number
  warm: boolean
}

const PARTICLE_COUNT = 100
const WARM_RATIO = 0.07
const SPEED_MIN = 0.35
const SPEED_MAX = 0.78
const SIZE_MIN = 70
const SIZE_MAX = 340
const SWAY_AMP_MIN = 6
const SWAY_AMP_MAX = 22
const SWAY_FREQ_MIN = 0.0006
const SWAY_FREQ_MAX = 0.0015
const PEAK_OPACITY_MIN = 0.03
const PEAK_OPACITY_MAX = 0.05
// Solid-core ratio: keep particles at full opacity out to this fraction of the
// radius, then fall off to transparent. Higher = sharper edge / less blur.
const CORE_STOP = 0.9
// Fraction of vertical travel spent fading in / out. Fade-out is longer so departures feel gradual.
const FADE_IN_FRACTION = 0.12
const FADE_OUT_FRACTION = 0.28

const canvasRef = ref<HTMLCanvasElement>()

let raf = 0
let resizeObserver: ResizeObserver | null = null
let themeObserver: MutationObserver | null = null
let onVisibilityChange: (() => void) | null = null
let onMotionChange: (() => void) | null = null
let reducedMotionMQ: MediaQueryList | null = null

onMounted(() => {
  const canvasEl = canvasRef.value
  if (!canvasEl) return
  const ctxEl = canvasEl.getContext('2d')
  if (!ctxEl) return
  setup(canvasEl, ctxEl)
})

function setup(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
  let width = 0
  let height = 0
  let seeded = false
  const particles: Particle[] = []
  let coolRGB = '138, 163, 207'
  let warmRGB = '255, 211, 115'

  const rand = (min: number, max: number) => min + Math.random() * (max - min)

  function smoothstep(t: number): number {
    return t * t * (3 - 2 * t)
  }

  function envelope(t: number): number {
    if (t <= 0 || t >= 1) return 0
    if (t < FADE_IN_FRACTION) return smoothstep(t / FADE_IN_FRACTION)
    if (t > 1 - FADE_OUT_FRACTION) return smoothstep((1 - t) / FADE_OUT_FRACTION)
    return 1
  }

  function readColors() {
    const css = getComputedStyle(canvas)
    const cool = css.getPropertyValue('--color-primary').trim()
    const warm = css.getPropertyValue('--color-gold').trim()
    // Probe canvas resolves any CSS color format (hex, rgb, hsl, named) to its rendered RGB triplet.
    const probe = document.createElement('canvas')
    probe.width = probe.height = 1
    const px = probe.getContext('2d')
    if (!px) return
    const toRGB = (color: string): string => {
      px.clearRect(0, 0, 1, 1)
      px.fillStyle = color
      px.fillRect(0, 0, 1, 1)
      const d = px.getImageData(0, 0, 1, 1).data
      return `${d[0]}, ${d[1]}, ${d[2]}`
    }
    if (cool) coolRGB = toRGB(cool)
    if (warm) warmRGB = toRGB(warm)
  }

  function resize() {
    const rect = canvas.getBoundingClientRect()
    width = rect.width
    height = rect.height
    if (width === 0 || height === 0) return
    const dpr = window.devicePixelRatio || 1
    canvas.width = Math.max(1, Math.floor(width * dpr))
    canvas.height = Math.max(1, Math.floor(height * dpr))
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    if (!seeded) {
      seed()
      seeded = true
      applyMotion()
    }
  }

  function resetParticle(p: Particle, scatterLifecycle: boolean) {
    const size = rand(SIZE_MIN, SIZE_MAX)
    const spawnY = height + rand(2, 16)
    // On first seed, spread particles across the canvas so the scene starts populated.
    const startProgress = scatterLifecycle ? Math.random() : 0
    p.baseX = rand(-size, width + size)
    p.size = size
    p.spawnY = spawnY
    p.y = spawnY - startProgress * (spawnY + size)
    p.vy = -rand(SPEED_MIN, SPEED_MAX)
    p.swayAmp = rand(SWAY_AMP_MIN, SWAY_AMP_MAX)
    p.swayFreq = (Math.random() < 0.5 ? -1 : 1) * rand(SWAY_FREQ_MIN, SWAY_FREQ_MAX)
    p.phase = rand(0, Math.PI * 2)
    p.peakOpacity = rand(PEAK_OPACITY_MIN, PEAK_OPACITY_MAX)
    p.warm = Math.random() < WARM_RATIO
  }

  function seed() {
    particles.length = 0
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const p: Particle = {
        baseX: 0, y: 0, spawnY: 0, vy: 0,
        swayAmp: 0, swayFreq: 0, phase: 0,
        size: 0, peakOpacity: 0, warm: false,
      }
      resetParticle(p, true)
      particles.push(p)
    }
  }

  function drawParticle(p: Particle, alphaScale = 1) {
    const progress = (p.spawnY - p.y) / (p.spawnY + p.size)
    const opacity = envelope(progress) * p.peakOpacity * alphaScale
    if (opacity <= 0.001) return
    const x = p.baseX + Math.sin(p.phase) * p.swayAmp
    const rgb = p.warm ? warmRGB : coolRGB
    const gradient = ctx.createRadialGradient(x, p.y, 0, x, p.y, p.size)
    gradient.addColorStop(0, `rgba(${rgb}, ${opacity})`)
    gradient.addColorStop(CORE_STOP, `rgba(${rgb}, ${opacity})`)
    gradient.addColorStop(1, `rgba(${rgb}, 0)`)
    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.arc(x, p.y, p.size, 0, Math.PI * 2)
    ctx.fill()
  }

  function step() {
    ctx.clearRect(0, 0, width, height)
    for (const p of particles) {
      p.phase += p.swayFreq
      p.y += p.vy
      if (p.y < -p.size - 4) {
        resetParticle(p, false)
        continue
      }
      drawParticle(p)
    }
    raf = requestAnimationFrame(step)
  }

  function stop() {
    if (raf) {
      cancelAnimationFrame(raf)
      raf = 0
    }
  }

  function start() {
    if (raf || !seeded) return
    raf = requestAnimationFrame(step)
  }

  function drawStatic() {
    ctx.clearRect(0, 0, width, height)
    for (const p of particles) drawParticle(p, 0.7)
  }

  function applyMotion() {
    if (!seeded) return
    stop()
    if (reducedMotionMQ?.matches || document.hidden) drawStatic()
    else start()
  }

  readColors()
  resize()

  resizeObserver = new ResizeObserver(resize)
  resizeObserver.observe(canvas)

  themeObserver = new MutationObserver(readColors)
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })

  onVisibilityChange = () => {
    if (document.hidden) stop()
    else applyMotion()
  }
  document.addEventListener('visibilitychange', onVisibilityChange)

  reducedMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)')
  onMotionChange = applyMotion
  reducedMotionMQ.addEventListener('change', onMotionChange)
}

onBeforeUnmount(() => {
  if (raf) cancelAnimationFrame(raf)
  resizeObserver?.disconnect()
  themeObserver?.disconnect()
  if (onVisibilityChange) document.removeEventListener('visibilitychange', onVisibilityChange)
  if (onMotionChange && reducedMotionMQ) reducedMotionMQ.removeEventListener('change', onMotionChange)
})
</script>

<style scoped>
.home-backdrop-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}
</style>
